import React, { useState, useEffect, useRef } from 'react';
import { Form, Modal, Button, Header, Message, Icon } from 'semantic';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { request } from 'utils/api';
import { BillingInfoFormikForm } from 'components/BillingInfo/formik-form';
import InputField from 'components/form-fields/formik/InputField';
import SearchDropdownField from 'components/form-fields/formik/SearchDropdown';

import MarketEnergyTariffs from 'components/MarketEnergyTariffs/Current';
import { EnergyMarketTariffProvider } from 'components/MarketEnergyTariffs/Context';
import { useAccountPlatformFeatures } from 'components/account-platform-features/context';

import { Trans } from 'react-i18next';
import { Layout } from 'components/Layout';

import {
  useInviteEmployeeFormInitialState,
  useInviteEmployeeFormValidationSchema,
  InviteEmployeeFormValues,
} from './formData';
import { useUser } from 'contexts/user';
import { useAccount } from 'contexts/account';
import { useFeatures } from 'contexts/features';
import {
  useSubmitBillingInfo,
  useSubmitInviteEmployee,
  useSubmitInviteEmployeeAndBilling,
} from './api';
import {
  InviteEmployeeErrorMessage,
  useInviteEmployeeErrorFormatter,
} from './errors';
import PriceField from 'components/form-fields/formik/PriceField';
import { formatCurrency } from 'utils/formatting';

import {
  PaymentMethodsWidgetCallbacks,
  tryStoringPaymentInstrument,
} from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import {
  PaymentMethodsWidgetProvider,
  useEnabledPaymentMethods,
} from 'contexts/enabledPaymentMethods';
import { useToast } from 'components/Toast';

type Props = {
  initialValues?: InviteEmployeeFormValues & { id?: string };
  trigger?: React.ReactNode;
  isOpen?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
};

export default function InviteEmployee({
  trigger,
  isOpen,
  onSuccess = () => {},
  onClose = () => {},
}: Props = {}) {
  const [open, setOpen] = useState(!!isOpen);
  const { t } = useTranslation();
  const { hasFeature, isReady: isFeatureReady } = useFeatures();
  const isBicCollectionEnabled = hasFeature('billing_bic_collection');

  const { accountHasPlatformFeature } = useAccountPlatformFeatures();
  const toast = useToast();

  const { user } = useUser();
  const { account, getAccount } = useAccount();

  const [submitted, setSubmitted] = useState(false);

  const formRef = useRef<FormikProps<InviteEmployeeFormValues> | null>(null);
  const formInitialState = useInviteEmployeeFormInitialState();

  const showAccountBilling = isFeatureReady && formInitialState.showBillingInfo;

  const errorFormatter = useInviteEmployeeErrorFormatter();
  const formValidationSchema = useInviteEmployeeFormValidationSchema(
    showAccountBilling,
    isBicCollectionEnabled
  );

  const { isPayBridgeEnabled, enabledPaymentMethods } =
    useEnabledPaymentMethods();

  const paymentProcessingRef = useRef<PaymentMethodsWidgetCallbacks>(null);

  useEffect(() => {
    setOpen(!!isOpen);
  }, [isOpen]);

  const onDataNeeded = async (body: any) =>
    request({
      path: '/1/users/search/fast',
      method: 'POST',
      body: {
        ...(body?.name && { searchPhrase: body.name }),
        accountId: user?.accountId,
      },
    }).then(({ data }) => {
      return {
        data: [
          {
            name: t(
              'evseInviteModal.forSomebodyElse',
              'For somebody else (new)'
            ),
            id: 'new',
          },
          ...data.filter((obj: any) => obj.id !== user?.id),
        ],
      };
    });

  // This is triggered when coming back from redirect, so I'm passing
  // Becuase this is a modal from semantic, this effect will be triggered even if the modal is closed
  // So I can send a toast on init.
  const submitBillingInfo = useSubmitBillingInfo({
    onSuccess: async () => {
      paymentProcessingRef.current?.authorizeActiveOption();
    },
    onError: (error) => {
      error.innerError.details?.forEach((detail) =>
        errorFormatter(detail, formRef.current)
      );

      toast.error(error.message);
    },
  });

  const submitInviteEmployeeAndBilling = useSubmitInviteEmployeeAndBilling(
    showAccountBilling,
    {
      onSuccess: async () => {
        if (onSuccess) {
          onSuccess();
        } else {
          setOpen(false);
        }
        await getAccount?.();
      },
      onError: (error) => {
        error.innerError.details?.forEach((detail) =>
          errorFormatter(detail, formRef.current)
        );
      },
    }
  );

  const submitInviteEmployee = useSubmitInviteEmployee({
    onSuccess: async () => {
      if (onSuccess) {
        onSuccess();
      } else {
        setOpen(false);
      }
      await getAccount?.();
    },
    onError: (error) => {
      error.innerError.details?.forEach((detail) =>
        errorFormatter(detail, formRef.current)
      );
    },
  });

  useEffect(() => {
    if (!user || !account || !enabledPaymentMethods || !isPayBridgeEnabled) {
      return;
    }

    tryStoringPaymentInstrument(account, enabledPaymentMethods).then(
      // Only store the invite if the payment method is stored
      (isStored) => {
        const invite = getInvite();

        if (isStored && isPayBridgeEnabled && invite) {
          invite.billing = undefined;

          submitInviteEmployee.mutate(invite);
          clearInvite();
        }
      }
    );
  }, [isPayBridgeEnabled, account, user, enabledPaymentMethods]);

  const onSubmit = (values: InviteEmployeeFormValues) => {
    if (isPayBridgeEnabled && showAccountBilling) {
      submitBillingInfo.mutate(values);
      saveInvite(values);
      return;
    }

    submitInviteEmployeeAndBilling.mutate(values);
    setSubmitted(true);
  };

  // we only allow dynamic employee reimbursement for NL accounts because market price is only available in NL
  const hasDynamicEmployeeReimbusement =
    account?.billing?.countryCode?.toUpperCase() === 'NL' &&
    accountHasPlatformFeature(
      'advanced-tariffs:dynamic-employee-reimbursement'
    );

  return (
    <Modal
      size="small"
      closeIcon
      trigger={trigger}
      onClose={() => {
        setOpen(false);
        onClose();
      }}
      onOpen={() => setOpen(true)}
      open={open}>
      <Modal.Header>
        {t('evseInviteModal.title', 'Invite employee')}
      </Modal.Header>
      <Formik
        innerRef={formRef}
        onSubmit={onSubmit}
        initialValues={{
          ...formInitialState.formData,
        }}
        validationSchema={formValidationSchema}>
        {({ values, handleSubmit, isSubmitting, resetForm }) => {
          return (
            <>
              <Modal.Content>
                <Trans i18nKey="evseInviteModal.descriptionNewLines">
                  <b>How it works</b>
                  <p>
                    An invitation e-mail will be send to the employee for
                    registration of the charging station.
                  </p>
                  <p>
                    After completing the registration, the charging station will
                    be visible in your account and the subscription will be
                    billed to your company.
                  </p>
                  <p>
                    The reimbursement of the charge sessions will be paid to the
                    employee every month.
                  </p>
                </Trans>
                {hasDynamicEmployeeReimbusement && (
                  <Layout horizontal style={{ marginBottom: '15px' }}>
                    <Button
                      basic
                      color={!values.enableEnergyMarketTracking ? 'blue' : null}
                      style={{
                        width: '100%',
                        height: '55px',
                        fontWeight: 'bold',
                      }}
                      onClick={() => {
                        resetForm({
                          values: {
                            ...values,
                            enableEnergyMarketTracking: false,
                            surchargePerKwh: undefined,
                          },
                        });
                      }}>
                      <Layout horizontal>
                        <Icon
                          style={{ marginRight: '1em' }}
                          name={
                            !values.enableEnergyMarketTracking
                              ? 'circle-dot'
                              : 'circle regular'
                          }
                        />
                        {t('evseInviteModal.pricePerKWh', 'Price per kWh')}
                      </Layout>
                    </Button>
                    <Button
                      basic
                      color={values.enableEnergyMarketTracking ? 'blue' : null}
                      style={{
                        width: '100%',
                        height: '55px',
                        fontWeight: 'bold',
                      }}
                      onClick={() => {
                        resetForm({
                          values: {
                            ...values,
                            enableEnergyMarketTracking: true,
                          },
                        });
                      }}>
                      <Layout horizontal>
                        <Icon
                          style={{ marginRight: '1em' }}
                          name={
                            values.enableEnergyMarketTracking
                              ? 'circle-dot'
                              : 'circle regular'
                          }
                        />
                        {t('evseInviteModal.dynamicTariff', 'Dynamic tariff')}
                      </Layout>
                    </Button>
                  </Layout>
                )}

                <Form onSubmit={handleSubmit}>
                  {submitInviteEmployee.isError && (
                    <InviteEmployeeErrorMessage
                      error={submitInviteEmployeeAndBilling.error}
                      onDismiss={() => submitInviteEmployeeAndBilling.reset()}
                    />
                  )}
                  {!values.enableEnergyMarketTracking && (
                    <PriceField
                      currency={account?.billingCurrency}
                      label={t('evseInviteModal.pricePerKwh')}
                      name="pricePerKwh"
                      required
                      wrapperStyle={{ marginBottom: '15px' }}
                    />
                  )}

                  {values.enableEnergyMarketTracking && (
                    <>
                      {/*
                    Area hardcoded to NL for now. This is a product decision and it means that
                    NL dynamic pricing will be applied to stations that use this tariff profile
                    even if they are not located in NL.

                    This might be changed in the future making the area selectable.
                    */}
                      <EnergyMarketTariffProvider energyDeliveryArea={'NL'}>
                        <MarketEnergyTariffs />
                      </EnergyMarketTariffProvider>
                      <Form.Field>
                        <label>
                          {t('evseInviteModal.surchargeTitle', 'Surcharge')}
                        </label>
                        <div>
                          {t(
                            'evseInviteModal.surchargePerKwhDescription',
                            'Set a surcharge to cover for additional costs the employee may incur on top of the market price.'
                          )}
                        </div>
                        <PriceField
                          currency={account?.billingCurrency}
                          name="surchargePerKwh"
                          wrapperStyle={{ marginBottom: '15px' }}
                        />
                      </Form.Field>
                      <Form.Field required>
                        <label>
                          {t(
                            'evseInviteModal.backupTarriffTitle',
                            'Backup tariff'
                          )}
                        </label>
                        <div>
                          {t(
                            'evseInviteModal.backupTarriffDescription',
                            ' Set your backup price in case the current energy market price is not available. The surcharge price from the dynamic tariff will still be applied.'
                          )}
                        </div>
                        <PriceField
                          currency={account?.billingCurrency}
                          name="pricePerKwh"
                          required
                        />
                        {values.surchargePerKwh && values.pricePerKwh && (
                          <div style={{ marginTop: '4px' }}>
                            {t(
                              'evseInviteModal.combinedTariffDescription',
                              '{{price}} per kWh including surcharge to reimburse.',
                              {
                                price: formatCurrency(
                                  Number(values.surchargePerKwh) +
                                    Number(values.pricePerKwh),
                                  account?.billingCurrency
                                ),
                              }
                            )}
                          </div>
                        )}
                      </Form.Field>
                    </>
                  )}

                  <SearchDropdownField
                    name="requestUserId"
                    label={t('evseInviteModal.employee', 'Employee')}
                    required
                    objectMode={false}
                    onDataNeeded={onDataNeeded}
                  />

                  {values.requestUserId === 'new' && (
                    <>
                      <InputField
                        name="requestUserFirstName"
                        label={t('formLabel.firstName', 'First Name')}
                        required
                        wrapperStyle={{ marginBottom: '15px' }}
                      />
                      <InputField
                        name="requestUserLastName"
                        label={t('formLabel.lastName', 'Last Name')}
                        required
                        wrapperStyle={{ marginBottom: '15px' }}
                      />
                      <InputField
                        name="requestUserEmail"
                        label={t('formLabel.email', 'Email Address')}
                        required
                        wrapperStyle={{ marginBottom: '15px' }}
                      />
                    </>
                  )}
                  {showAccountBilling && isFeatureReady && (
                    <>
                      <Message>
                        <Message.Header>
                          {t(
                            'evseInviteModal.billingInfoRequired',
                            'Billing Info Required'
                          )}
                        </Message.Header>
                        <Message.Content>
                          {t(
                            'evseInviteModal.billingInfoRequiredDescription',
                            `Please fill in your account billing info to setup your employees subscription.`
                          )}
                        </Message.Content>
                      </Message>
                      <Header textAlign="left" as="h3">
                        {t(
                          'addCardModal.billingDetailsHeader',
                          'Billing Details'
                        )}
                      </Header>
                      <PaymentMethodsWidgetProvider ref={paymentProcessingRef}>
                        <div
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '5px',
                          }}>
                          <BillingInfoFormikForm
                            enableBicCollection={isBicCollectionEnabled}
                            accountType={account?.type || 'individual'}
                            enableCountryCodeInput={
                              !formInitialState.formData.billing.countryCode
                            }
                            objectPath="billing"
                            redirectUrl={window.location.href}
                          />
                        </div>
                      </PaymentMethodsWidgetProvider>
                    </>
                  )}
                </Form>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  basic
                  onClick={() => {
                    setOpen(false);
                  }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  primary
                  floated="right"
                  loading={isSubmitting && !submitted}
                  disabled={isSubmitting && !submitted}
                  onClick={handleSubmit}
                  content={t('common.invite', 'Invite')}
                />
              </Modal.Actions>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
}

function saveInvite(invite: InviteEmployeeFormValues) {
  localStorage.setItem('employeeInvite', JSON.stringify(invite));
}

function getInvite(): InviteEmployeeFormValues | undefined {
  const invite = localStorage.getItem('employeeInvite');
  if (invite) {
    return JSON.parse(invite);
  }
  return undefined;
}

function clearInvite() {
  localStorage.removeItem('employeeInvite');
}
